import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail, ChevronRight, CheckCircle2 } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageTitle, Card, BigButton } from "@/components/Layout";
import { AreaField } from "@/components/Fields";
import { PhotoInput } from "@/components/PhotoInput";
import { StatusBadge } from "@/components/Status";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ContactButtons = () => {
  const { meta, t } = useApp();
  const c = meta?.contact || {};
  const missing = (v) => !v || v.includes("[");
  const go = (v, url) => (missing(v) ? toast(t("Contact details will be added soon.")) : window.open(url, "_blank"));
  const digits = (v) => (v || "").replace(/\D/g, "");
  const items = [
    ["whatsapp", MessageCircle, "WhatsApp", () => go(c.whatsapp, `https://wa.me/${digits(c.whatsapp)}`), "bg-[#25D366]"],
    ["phone", Phone, "Call", () => go(c.phone, `tel:${c.phone}`), "bg-[#0D3B2B]"],
    ["email", Mail, "Email", () => go(c.email, `mailto:${c.email}`), "bg-[#E05C3A]"],
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map(([id, Icon, label, fn, bg]) => (
        <button key={id} onClick={fn} data-testid={`contact-${id}-btn`}
          className={`flex h-20 flex-col items-center justify-center gap-1 rounded-2xl font-semibold text-white active:scale-95 transition-transform ${bg}`}>
          <Icon className="h-6 w-6" />{t(label)}
        </button>
      ))}
    </div>
  );
};

const ComplaintForm = ({ bookings, initialBooking, onDone }) => {
  const { meta, t } = useApp();
  const [f, setF] = useState({ booking_id: initialBooking || "none", category: "", message: "", photo_path: "" });
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const submit = async () => {
    if (!f.category) return toast.error(t("Please choose what the problem is about."));
    if (f.message.trim().length < 5) return toast.error(t("Please describe the problem in a few words."));
    setBusy(true);
    try {
      const { data } = await api.post("/complaints", { ...f, booking_id: f.booking_id === "none" ? "" : f.booking_id });
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="space-y-4" data-testid="complaint-form">
      <h2 className="font-display text-lg font-bold text-[#0D3B2B]">{t("Raise a complaint")}</h2>
      <Select value={f.booking_id} onValueChange={(v) => set({ booking_id: v })}>
        <SelectTrigger className="h-12 rounded-xl" data-testid="complaint-booking-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{t("Not about a specific booking")}</SelectItem>
          {bookings.map((b) => <SelectItem key={b.booking_id} value={b.booking_id}>{b.booking_id} · {b.date}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex flex-wrap gap-2">
        {(meta?.complaint_categories || []).map((c) => (
          <button key={c} type="button" onClick={() => set({ category: c })} data-testid={`complaint-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`rounded-full border-2 px-4 py-2 text-sm font-semibold ${f.category === c ? "border-[#E05C3A] bg-[#E05C3A]/10 text-[#0D3B2B]" : "border-[#D2E0D8] text-[#4A6358]"}`}>{t(c)}</button>
        ))}
      </div>
      <AreaField label={t("What happened?")} testid="complaint-message-input" value={f.message} onChange={(e) => set({ message: e.target.value })} />
      <PhotoInput value={f.photo_path} onChange={(p) => set({ photo_path: p })} testid="complaint-photo" />
      <BigButton coral onClick={submit} disabled={busy} data-testid="complaint-submit-btn">{busy ? t("Sending…") : t("Send complaint")}</BigButton>
    </Card>
  );
};

export default function Help() {
  const { t } = useApp();
  const [params] = useSearchParams();
  const [list, setList] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [created, setCreated] = useState(null);
  const load = () => api.get("/complaints").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-5">
      <PageTitle sub={t("We're here to help. Reach us directly or send a complaint.")}>{t("Help and complaints")}</PageTitle>
      <ContactButtons />
      {created ? (
        <Card className="space-y-2 text-center" data-testid="complaint-created">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[#2E9E6B]" />
          <p className="font-display text-lg font-bold">{t("Complaint received")}</p>
          <p className="text-sm text-[#4A6358]">{t("Complaint ID")}: <b data-testid="complaint-created-id">{created.complaint_id}</b></p>
          <button className="font-semibold text-[#2E9E6B]" onClick={() => setCreated(null)} data-testid="complaint-new-btn">{t("Raise another")}</button>
        </Card>
      ) : (
        <ComplaintForm bookings={bookings} initialBooking={params.get("booking")} onDone={(c) => { setCreated(c); load(); }} />
      )}
      {list.length > 0 && (
        <section className="space-y-2" data-testid="complaints-list">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#7B968B]">{t("My complaints")}</h2>
          {list.map((c) => (
            <Link key={c.complaint_id} to={`/help/${c.complaint_id}`} data-testid={`complaint-item-${c.complaint_id}`}
              className="flex items-center gap-3 rounded-2xl border border-[#D2E0D8] bg-white p-4">
              <div className="flex-1">
                <p className="font-semibold text-[#0D3B2B]">{c.complaint_id} · {t(c.category)}</p>
                <p className="text-xs text-[#7B968B]">{fmtDateTime(c.updated_at)} · {c.messages.length} {t("messages")}</p>
              </div>
              <StatusBadge status={c.status} /><ChevronRight className="h-5 w-5 text-[#7B968B]" />
            </Link>
          ))}
        </section>
      )}
    </div>
  );
}
